import publicRouteConfig from "./publicRoutes.json";

export type SupportedLanguage = "en" | "da" | "sv";
export type Hreflang = SupportedLanguage | "x-default";

export type PublicRoute = {
  path: string;
  changefreq: string;
  priority: number;
  languages?: SupportedLanguage[];
};

export type PublicRouteMatch = {
  language: SupportedLanguage;
  route: PublicRoute;
};

export type HreflangLink = {
  hreflang: Hreflang;
  href: string;
};

export type SitemapEntry = {
  loc: string;
  changefreq: string;
  priority: number;
  alternates: HreflangLink[];
};

const trimTrailingSlash = (value: string) => value.replace(/\/+$/, "");

export const SITE_URL = trimTrailingSlash(publicRouteConfig.siteUrl);
export const SITEMAP_URL = `${SITE_URL}/sitemap.xml`;
export const DEFAULT_LANGUAGE =
  publicRouteConfig.defaultLanguage as SupportedLanguage;
export const SUPPORTED_LANGUAGES =
  publicRouteConfig.supportedLanguages as SupportedLanguage[];
export const PUBLIC_ROUTES = publicRouteConfig.routes as PublicRoute[];

const routePath = (path: string) => path.replace(/^\/+|\/+$/g, "");

export const normalizePathname = (pathname: string) => {
  const [pathOnly] = pathname.split(/[?#]/);
  const normalized = pathOnly.replace(/^\/+|\/+$/g, "");
  return normalized ? `/${normalized}` : "/";
};

export const toLocalizedPathname = (
  language: SupportedLanguage,
  path: string
) => {
  const cleanPath = routePath(path);
  return cleanPath ? `/${language}/${cleanPath}` : `/${language}`;
};

export const toAbsoluteUrl = (pathname: string) => {
  const normalizedPathname = normalizePathname(pathname);
  return normalizedPathname === "/"
    ? `${SITE_URL}/`
    : `${SITE_URL}${normalizedPathname}`;
};

export const getRouteLanguages = (route: PublicRoute) =>
  route.languages ?? SUPPORTED_LANGUAGES;

export const getPublicRoutePathnames = () =>
  PUBLIC_ROUTES.flatMap((route) =>
    getRouteLanguages(route).map((language) =>
      toLocalizedPathname(language, route.path)
    )
  );

export const getPublicRouteMatch = (
  pathname: string
): PublicRouteMatch | null => {
  const normalizedPathname = normalizePathname(pathname);
  const match = normalizedPathname.match(/^\/([^/]+)(?:\/(.*))?$/);

  if (!match) return null;

  const language = match[1] as SupportedLanguage;
  if (!SUPPORTED_LANGUAGES.includes(language)) return null;

  const path = routePath(match[2] ?? "");
  const route = PUBLIC_ROUTES.find((candidate) => routePath(candidate.path) === path);
  if (!route || !getRouteLanguages(route).includes(language)) return null;

  return { language, route };
};

export const isIndexablePublicPathname = (pathname: string) =>
  getPublicRouteMatch(pathname) !== null;

export const getCanonicalUrlForPathname = (pathname: string) => {
  const match = getPublicRouteMatch(pathname);
  if (!match) return null;

  return toAbsoluteUrl(toLocalizedPathname(match.language, match.route.path));
};

export const getHreflangLinksForRoute = (route: PublicRoute) => {
  const languages = getRouteLanguages(route);
  const defaultHreflang = languages.includes(DEFAULT_LANGUAGE)
    ? DEFAULT_LANGUAGE
    : languages[0];
  const links: HreflangLink[] = languages.map((language) => ({
    hreflang: language,
    href: toAbsoluteUrl(toLocalizedPathname(language, route.path)),
  }));

  links.push({
    hreflang: "x-default",
    href: toAbsoluteUrl(toLocalizedPathname(defaultHreflang, route.path)),
  });

  return links;
};

export const getHreflangLinksForPathname = (pathname: string) => {
  const match = getPublicRouteMatch(pathname);
  return match ? getHreflangLinksForRoute(match.route) : [];
};

export const getSitemapEntries = (): SitemapEntry[] =>
  PUBLIC_ROUTES.flatMap((route) =>
    getRouteLanguages(route).map((language) => ({
      loc: toAbsoluteUrl(toLocalizedPathname(language, route.path)),
      changefreq: route.changefreq,
      priority: route.priority,
      alternates: getHreflangLinksForRoute(route),
    }))
  );
